import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { capitalize } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, Text } from '@node-space/ui-components'
import { Country } from 'types/authentication/Profile'
import { IndividualCustomerType } from 'types/customers'
import { CustomerDetailRow } from './AddCustomer/CustomerDetailRow'

interface IndividualCustomerSidePanelViewProps {
  customer: IndividualCustomerType
  countries: Country[]
  isCountriesLoading: boolean
  isCountriesLoadingError: boolean
}

export const IndividualCustomerSidePanelView = ({
  customer,
  countries,
  isCountriesLoading,
  isCountriesLoadingError,
}: IndividualCustomerSidePanelViewProps) => {
  const { t } = useTranslation()

  const { details, person } = customer?.individual || {}
  const { birthCountryCode, documentNumber } = details
  const { firstName, lastName, dateOfBirth, address } = person
  const { addressLine1, addressLine2, city, postalCode, countryCode } = address || {}

  const nationalityCountry = useMemo(() => {
    return countries?.find(country => {
      if (country?.code === birthCountryCode) {
        return country
      }
    })
  }, [countries, customer])

  const residenceCountry = useMemo(() => {
    return countries?.find(country => {
      if (country?.code === countryCode) {
        return country
      }
    })
  }, [countries, customer])

  return (
    <>
      {customer?.type && (
        <CustomerDetailRow
          label={t('customers.type')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {capitalize(customer?.type?.toLowerCase())}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}
      {firstName && (
        <CustomerDetailRow
          label={t('firstName')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {firstName}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}

      {lastName && (
        <CustomerDetailRow
          label={t('lastName')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {lastName}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}

      {documentNumber && (
        <CustomerDetailRow
          label={t('customers.personalIdNumber')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {documentNumber}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}

      {dateOfBirth && (
        <CustomerDetailRow
          label={t('formFields.dateOfBirth')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {dayjs(dateOfBirth).format('DD-MM-YYYY')}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}

      {birthCountryCode &&
        nationalityCountry &&
        !isCountriesLoadingError &&
        !isCountriesLoading && (
          <CustomerDetailRow
            label={t('customers.countryOfNationality')}
            detail={
              <Box flex isFlexWrap className="lg:justify-end">
                <Text variant="body-label" className="text-left lg:text-right" tag="span">
                  {nationalityCountry?.name || ''}
                </Text>
              </Box>
            }
            isColumn={false}
          />
        )}

      <Text variant="body-label">{t('customers.residentialAddress')}</Text>
      {addressLine1 && (
        <CustomerDetailRow
          label={t('customers.address.address')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {`${addressLine1 || ''}, ${addressLine2 || ''}, ${city || ''}, ${postalCode || ''}`}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}
      {countryCode && residenceCountry && (
        <CustomerDetailRow
          label={t('customers.country')}
          detail={
            <Box flex isFlexWrap className="lg:justify-end">
              <Text variant="body-label" className="text-left lg:text-right" tag="span">
                {residenceCountry?.name}
              </Text>
            </Box>
          }
          isColumn={false}
        />
      )}
    </>
  )
}
